import { useTranslation } from "react-i18next";
import { App, Divider } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useState } from "react";

import { usePatchDataBlockMutation } from "@/redux/service";
import { useEvidenceContributors } from "@/domains/evidence/hooks/useEvidenceFields";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useDataBlockPermissions } from "@/domains/datablocks/hooks/useDataBlockPermissions";
import { BaseUserResponse } from "@/redux/users/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import UserAvatar from "@/domains/users/components/UserAvatar";
import DataBlockReferences from "@/features/datablocks/details/components/DataBlockReferences";
import AddContributorsModal from "@/features/datablocks/details/components/AddContributorsModal";
import { DataBlockSidebarProps } from "@/features/datablocks/details/types";

import "../styles.css";

export const DataBlockSidebar = ({
  dataBlock,
  dataTemplate,
  showCtas,
}: DataBlockSidebarProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [patchDataBlock] = usePatchDataBlockMutation();
  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const contributors = useEvidenceContributors(dataBlock);
  const currentUser = useSelector(selectCurrentUser);
  const { canEdit } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const deleteContributor = (deletedContributorId: number) => {
    const data = {
      fieldValues: [
        {
          field: dataBlock.fieldsMapping.contributors,
          value: (contributors.data as BaseUserResponse[])
            .filter((i) => i.id !== deletedContributorId)
            .map((i) => i.id),
        },
      ],
    };
    patchDataBlock({ id: dataBlock.id, data })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("datablocks.labels.contributorsDeleted"),
        }),
      );
  };

  return (
    <div className="data-block-sidebar">
      <div className="data-block-sidebar-contributors">
        <div className="data-block-sidebar-contributors-header">
          <h2 className="header-m-medium">
            {t("datablocks.labels.contributors")}
          </h2>

          {canEdit && showCtas && (
            <BaseButton
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<PlusOutlined />}
              onClick={() => setContributorModalOpen(true)}
            >
              {t("datablocks.labels.addContributors")}
            </BaseButton>
          )}
        </div>

        <div className="data-block-sidebar-contributors-items-container">
          {contributors.isLoading ? (
            <div className="data-block-sidebar-contributors-items-container">
              <BaseSpinner size="small" />
            </div>
          ) : (
            (contributors.data as BaseUserResponse[]).map((contributor) => (
              <div
                key={contributor.id}
                className="data-block-sidebar-contributors-items"
              >
                <div className="data-block-sidebar-contributors-items-details">
                  <UserAvatar user={contributor} />
                  <span className="data-block-sidebar-contributors-items-names">
                    {contributor.fullName}
                  </span>
                </div>

                {canEdit && showCtas && (
                  <DeleteOutlined
                    onClick={() => deleteContributor(contributor.id)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>
      <Divider />
      <DataBlockReferences dataBlock={dataBlock} />
      <AddContributorsModal
        open={contributorModalOpen}
        setOpen={setContributorModalOpen}
        dataBlock={dataBlock}
        currentContributorIds={
          contributors.isLoading
            ? []
            : (contributors.data as BaseUserResponse[]).map((i) => i.id)
        }
      />
    </div>
  );
};
